import React, { useEffect, useRef, useState } from "react";
import { getProductFaqs, saveProductFaqs } from "../../api/productFaqApi";
import "./AdminProductFaqs.css";

// Keep in step with the limits in the backend (productFaq.controller.js).
export const MAX_FAQS = 50;
export const MAX_QUESTION = 300;
export const MAX_ANSWER = 5000;

const isBlank = (row) => !row.question.trim() && !row.answer.trim();

// Same rule as the server: blank rows are ignored, half-filled rows are an error.
export const checkRows = (rows) => {
  for (let i = 0; i < rows.length; i += 1) {
    const { question, answer } = rows[i];
    if (isBlank(rows[i])) continue;
    if (!question.trim()) return `FAQ ${i + 1} is missing its question`;
    if (!answer.trim()) return `FAQ ${i + 1} is missing its answer`;
  }
  return null;
};

const snapshot = (rows) => JSON.stringify(rows.filter((r) => !isBlank(r)).map((r) => [r.question.trim(), r.answer.trim()]));

// The editor for ONE product's FAQs. The list is saved as a whole, in the order shown.
export default function AdminProductFaqs({ product, onClose, showFeedback }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const saved = useRef("[]"); // what the server has, to know whether anything changed
  const nextKey = useRef(0);

  const newRow = (question = "", answer = "") => ({ key: (nextKey.current += 1), question, answer });

  useEffect(() => {
    let live = true;
    getProductFaqs(product.id).then((result) => {
      if (!live) return;
      if (result.ok && result.success) {
        const loaded = (result.data || []).map((f) => newRow(f.question, f.answer));
        setRows(loaded);
        saved.current = snapshot(loaded);
      } else {
        setError(result.message || "Unable to load the FAQs");
      }
      setLoading(false);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  const dirty = snapshot(rows) !== saved.current;

  const close = () => {
    if (dirty && !window.confirm("You have unsaved changes. Close without saving?")) return;
    onClose();
  };

  const patch = (key, change) => setRows((list) => list.map((r) => (r.key === key ? { ...r, ...change } : r)));
  const remove = (key) => setRows((list) => list.filter((r) => r.key !== key));
  const move = (index, step) =>
    setRows((list) => {
      const target = index + step;
      if (target < 0 || target >= list.length) return list;
      const next = [...list];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  const add = () => setRows((list) => [...list, newRow()]);

  const save = async (e) => {
    e.preventDefault();
    const problem = checkRows(rows);
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setSaving(true);
    const result = await saveProductFaqs(
      product.id,
      rows.filter((r) => !isBlank(r)).map((r) => ({ question: r.question.trim(), answer: r.answer.trim() }))
    );
    setSaving(false);
    if (result.ok && result.success) {
      showFeedback("FAQs saved successfully!", "success");
      onClose();
    } else {
      setError(result.message || "Unable to save the FAQs");
    }
  };

  return (
    <div className="options-modal-overlay" onClick={close}>
      <div className="options-modal pfaqA-modal" role="dialog" aria-label={`FAQs for ${product.name}`} onClick={(e) => e.stopPropagation()}>
        <div className="options-modal-header">
          <h3>FAQs — {product.name}</h3>
          <button type="button" className="options-modal-close" aria-label="Close" onClick={close}>
            &times;
          </button>
        </div>

        {loading ? (
          <p>Loading FAQs...</p>
        ) : (
          <form onSubmit={save}>
            <p className="pfaqA-hint">
              These questions appear only on this product's page, in the order shown here. {rows.length} of {MAX_FAQS} used.
            </p>

            {rows.length === 0 && <p className="pfaqA-empty">No FAQs yet for this product.</p>}

            {rows.map((row, index) => (
              <div className="pfaqA-row" key={row.key}>
                <div className="pfaqA-row-head">
                  <strong>FAQ {index + 1}</strong>
                  <span className="pfaqA-tools">
                    <button type="button" aria-label={`Move FAQ ${index + 1} up`} disabled={index === 0} onClick={() => move(index, -1)}>
                      ↑
                    </button>
                    <button type="button" aria-label={`Move FAQ ${index + 1} down`} disabled={index === rows.length - 1} onClick={() => move(index, 1)}>
                      ↓
                    </button>
                    <button type="button" className="pfaqA-remove" aria-label={`Remove FAQ ${index + 1}`} onClick={() => remove(row.key)}>
                      Remove
                    </button>
                  </span>
                </div>
                <label>
                  Question
                  <input value={row.question} maxLength={MAX_QUESTION} onChange={(e) => patch(row.key, { question: e.target.value })} />
                </label>
                <label>
                  Answer
                  <textarea rows="4" value={row.answer} maxLength={MAX_ANSWER} onChange={(e) => patch(row.key, { answer: e.target.value })} />
                </label>
              </div>
            ))}

            <button type="button" className="pfaqA-add" disabled={rows.length >= MAX_FAQS} onClick={add}>
              + Add FAQ
            </button>

            {error && (
              <div className="pfaqA-error" role="alert">
                {error}
              </div>
            )}

            <div className="pfaqA-actions">
              <button type="button" className="pfaqA-cancel" onClick={close}>
                Cancel
              </button>
              <button type="submit" className="pfaqA-save" disabled={saving || !dirty}>
                {saving ? "Saving..." : "Save FAQs"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
